// Shared site facts for the policy pages, landing page and footer (single source for copy that must match checkout).
import {BRAND, PRICES, METALS, CHAIN_LENGTHS} from './config.js'

export const SUPPORT_EMAIL = 'support@pendspin.com'
// Cloudflare rewrites plain emails to "[email protected]"; email_off comments stop that.
export const SUPPORT_LINK = `<!--email_off--><a href="mailto:${SUPPORT_EMAIL}">${SUPPORT_EMAIL}</a><!--/email_off-->`
export const LEGAL_UPDATED = 'October 20, 2026'

/** Footer / sitemap links, in display order. */
export const FOOTER_LINKS = [
  {href: '/pricing', label: 'Pricing'},
  {href: '/shipping', label: 'Shipping'},
  {href: '/refund', label: 'Refunds'},
  {href: '/terms', label: 'Terms'},
  {href: '/privacy', label: 'Privacy'},
  {href: '/contact', label: 'Contact'},
]

export const usd = (/** @type {number} */ cents) => `$${(cents / 100).toFixed(2).replace(/\.00$/, '')}`

/** Footer HTML (also used by the static pages). */
export const footerHtml = () =>
  `<footer class="site-footer" data-testid="site-footer"><nav aria-label="Footer">${FOOTER_LINKS.map(
    l => `<a href="${l.href}">${l.label}</a>`,
  ).join(
    '',
  )}</nav><p>&copy; 2026 ${BRAND} · ${SUPPORT_LINK} · Custom pendant necklaces made to order · Prices in USD</p></footer>`

const metalsList = METALS.map(m => m.label.toLowerCase()).join(', ')
const chainRange = `${CHAIN_LENGTHS[0]}–${CHAIN_LENGTHS[CHAIN_LENGTHS.length - 1]} inches`
const word = usd(PRICES.word)

/** @type {Record<string, {title: string, h1: string, body: string}>} */
export const PAGES = {
  '/pricing': {
    title: `Pricing · ${BRAND}`,
    h1: 'Pricing',
    body: `
<p>All prices are in US dollars (USD). Shipping within the United States is included. Sales tax, if any, is shown at checkout.</p>
<table data-testid="price-table"><thead><tr><th>Metal</th><th>Chain length</th><th>Price</th></tr></thead><tbody>
${METALS.map(m => `<tr><td>${m.label}</td><td>${chainRange} (${CHAIN_LENGTHS.join(', ')})</td><td>${word}</td></tr>`).join('\n')}
</tbody></table>
<p>The price is the same for every metal (${metalsList}), every chain length and every font: <b>${word}</b> for a personalized word pendant necklace, with free US shipping.</p>
<p>Each pendant is made to order from your word, so please check the preview carefully before paying. See our <a href="/refund">refund policy</a> and <a href="/shipping">shipping page</a>. <a href="/design">Design yours</a>.</p>`,
  },
  '/contact': {
    title: `Contact · ${BRAND}`,
    h1: 'Contact us',
    body: `
<p>Questions about an order, a design or a delivery? Email us and a person will reply.</p>
<p><b>Support email:</b> ${SUPPORT_LINK}</p>
<p><b>Response time:</b> within 2 business days (usually sooner).</p>
<p>Please include your order number (shown on the confirmation page after checkout) and, for a problem with an item, a photo.</p>
<p>${BRAND} is an online-only business; we do not have a storefront.</p>`,
  },
  '/refund': {
    title: `Refund & Return Policy · ${BRAND}`,
    h1: 'Refund and return policy',
    body: `
<p>Every ${BRAND} pendant is personalized and made to order, so it cannot be resold once production starts. This policy explains what we can do.</p>
<h2>Cancel before production</h2>
<p>You can cancel for a full refund within <b>6 hours of placing your order</b>, before production begins. Email ${SUPPORT_LINK} with your order number as soon as possible. After that window the item is in production and the order can no longer be cancelled.</p>
<h2>Defective, damaged or wrong item</h2>
<p>If your pendant arrives damaged, defective, or does not match what you ordered (wrong word, font, metal or chain length), contact us within <b>14 days of delivery</b> with your order number and a photo. We will, at your choice, send a free replacement or refund the order in full. You do not need to return a defective or incorrect item.</p>
<h2>Change of mind</h2>
<p>Because items are made to your specification, we do not accept returns or give refunds for change of mind, or for spelling that matches what you entered at checkout. Please check your word in the preview before paying.</p>
<h2>Lost packages</h2>
<p>If tracking shows your order was lost in transit, contact us and we will replace it or refund it.</p>
<h2>How refunds work</h2>
<p>We reply within 2 business days. Approved refunds go back to your original payment method through Stripe and usually appear within 5 to 10 business days, depending on your bank.</p>`,
  },
  '/shipping': {
    title: `Shipping & Fulfillment · ${BRAND}`,
    h1: 'Shipping and fulfillment',
    body: `
<p>${BRAND} sells personalized 3D-previewed pendant necklaces. Your pendant is <b>produced and shipped by a third-party jeweler partner</b> that makes each piece to order; ${BRAND} does not hold stock.</p>
<h2>Timing</h2>
<p>Standard production and delivery usually takes about <b>10 to 12 business days</b> from the time you order. Delivery windows are estimates, not guarantees, and can be affected by carrier delays and holidays.</p>
<h2>Destinations and cost</h2>
<p>We currently ship to addresses in the <b>United States</b> only. Shipping is free.</p>
<h2>Tracking</h2>
<p>When your order ships, we email you the tracking details if the carrier provides them. If you have not heard from us after 12 business days, email ${SUPPORT_LINK}.</p>
<h2>Address accuracy</h2>
<p>Please double-check your shipping address at checkout. To fix a mistake, email us right away; we cannot change an address after the order has shipped.</p>
<p>See also our <a href="/refund">refund policy</a>.</p>`,
  },
  '/terms': {
    title: `Terms of Sale · ${BRAND}`,
    h1: 'Terms of sale',
    body: `
<p>Last updated ${LEGAL_UPDATED}. By placing an order on this website you agree to these terms.</p>
<h2>Who we are</h2><p>${BRAND} (pendspin.com) is an online seller of personalized pendant necklaces. Contact: ${SUPPORT_LINK}.</p>
<h2>Products and preview</h2><p>Pendants are made to order from the word, font, metal and chain length you choose. The 3D preview is an illustration; real metal color, size (a few millimeters across) and letter shapes may vary slightly from the screen.</p>
<h2>Prices and payment</h2><p>Prices are in USD and shown on the <a href="/pricing">pricing page</a>. Payment is taken at checkout by Stripe. We may refuse or cancel an order, with a full refund, if we cannot make it or suspect fraud.</p>
<h2>Content you provide</h2><p>You confirm you have the right to use the text you submit and that it is not unlawful or infringing. We may decline to produce items that are.</p>
<h2>Shipping, returns and refunds</h2><p>See <a href="/shipping">Shipping</a> and <a href="/refund">Refunds</a>; they are part of these terms.</p>
<h2>Liability</h2><p>To the extent the law allows, our liability for any order is limited to the amount you paid for it. Nothing here limits rights you have under consumer law.</p>
<h2>Changes</h2><p>We may update these terms; the version in force when you order applies to that order.</p>`,
  },
  '/privacy': {
    title: `Privacy Policy · ${BRAND}`,
    h1: 'Privacy policy',
    body: `
<p>Last updated ${LEGAL_UPDATED}.</p>
<h2>What we collect</h2><p>When you order we collect: the text (or shape) you want on the pendant and your design choices, a preview image of it, your name, email address and shipping address, and order status. Payment card details are entered on Stripe's page; we never see or store your card number.</p>
<h2>How we use it</h2><p>To make and ship your order (including sharing the design and shipping details with our jeweler partner and the carrier), to process payment and refunds through Stripe, to email you about your order, and to answer support requests.</p>
<h2>Who we share it with</h2><p>Stripe (payments), our production and shipping partner, and service providers that host this site. <b>We do not sell your personal data.</b></p>
<h2>Retention and your rights</h2><p>We keep order records as long as needed for support, accounting and legal reasons. To access, correct or delete your data, email ${SUPPORT_LINK}.</p>
<h2>Cookies</h2><p>We use only what is needed to run the site (for example remembering light or dark theme in your browser) and what Stripe needs for checkout.</p>`,
  },
}

export const STATIC_PAGE_CSS = `*{box-sizing:border-box}body{margin:0;font:16px/1.6 system-ui,sans-serif;background:#14110d;color:#f3ece0;min-height:100vh;display:flex;flex-direction:column}
header.site-header{padding:20px 24px;letter-spacing:.28em;text-transform:uppercase;font-size:14px}header.site-header a{color:#e3c981;text-decoration:none}
main.doc{flex:1;width:100%;max-width:760px;margin:0 auto;padding:8px 24px 40px}h1{font-family:Georgia,serif;font-weight:500;font-size:34px}h2{font-size:18px;margin:28px 0 4px;color:#e3c981}
a{color:#f3d995}table{width:100%;border-collapse:collapse;margin:16px 0}th,td{text-align:left;padding:10px 8px;border-bottom:1px solid #3a3226}
.site-footer{padding:24px;text-align:center;font-size:13px;color:#a99e86;border-top:1px solid #2c261c}.site-footer nav{display:flex;flex-wrap:wrap;gap:8px 18px;justify-content:center;margin-bottom:8px}.site-footer a{color:#d9cfba}
@media(max-width:600px){h1{font-size:28px}main.doc{padding:4px 18px 32px}}`

/**
 * Full HTML document for a policy page.
 * @param {{title: string, h1: string, body: string}} p
 */
export const renderPage = p =>
  `<!doctype html><html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><title>${p.title}</title><meta name="description" content="${p.h1} for ${BRAND}, custom pendant necklaces."><style>${STATIC_PAGE_CSS}</style></head><body><header class="site-header"><a href="/">${BRAND}</a></header><main class="doc"><h1>${p.h1}</h1>${p.body}</main>${footerHtml()}</body></html>`
